import { useEffect } from 'react';
import type { JSX } from 'react';
import { injectHeadScript } from '../util/inject-head-script';
import {
    claimBoot,
    getAdvertisingWindow,
    type AdvertisingWindow,
    type FbqStub,
} from '../util/advertising-window';
import { isConfiguredPixelId } from './is-configured-pixel-id';
/* eslint-disable jsx-a11y/alt-text */

const FB_EVENTS_SRC = 'https://connect.facebook.net/en_US/fbevents.js';

interface FacebookTrackerProps {
    pixel: string;
    pixelClient: string;
}

/**
 * Return `window.fbq`, creating Meta's canonical stub if it does not exist yet.
 * An existing `fbq` is reused rather than replaced: Business Manager pages load
 * the pixel from a Silverstripe template, and a GTM container may define it too.
 *
 * `_fbq` must point at the same object as `fbq`, or `fbevents.js` reports a
 * conflicting-versions diagnostic.
 */
const getOrCreateFbq = (w: AdvertisingWindow): FbqStub => {
    const existing = w.fbq;
    if (existing) return existing;

    function fbq(...args: unknown[]): void {
        if (fbq.callMethod) {
            fbq.callMethod(...args);
        } else {
            fbq.queue.push(args);
        }
    }

    fbq.callMethod = undefined as FbqStub['callMethod'];
    fbq.queue = [] as unknown[][];
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.push = fbq;

    w.fbq = fbq;
    w._fbq = w._fbq ?? fbq;
    return fbq;
};

/**
 * Boot the Meta pixel for every configured id.
 *
 * Empty ids and the `foo` placeholder are skipped, so `init` only ever receives
 * a real pixel id. Re-initialising an id Meta has already registered is not
 * counted twice, but it does raise a duplicate-pixel diagnostic, so each id is
 * claimed once per document.
 */
export const loadFacebookPixel = (pixelIds: string[]): void => {
    const w = getAdvertisingWindow();
    const ids = pixelIds
        .filter(isConfiguredPixelId)
        .filter((id) => claimBoot(w, `facebook:${id}`));

    if (ids.length === 0) return;

    const fbq = getOrCreateFbq(w);
    for (const id of ids) {
        fbq('init', id);
    }

    injectHeadScript(FB_EVENTS_SRC);
};

export const FacebookPixelsHead: React.FC<FacebookTrackerProps> = ({
    pixel,
    pixelClient,
}) => {
    // The pixel is injected after hydration (see `injectHeadScript`).
    useEffect(() => {
        loadFacebookPixel([pixel, pixelClient]);
    }, [pixel, pixelClient]);

    return null;
};

export const FacebookPixelsBody = ({
    pixel,
    pixelClient,
}: FacebookTrackerProps): JSX.Element => {
    const pixelIds = [pixel, pixelClient].filter(isConfiguredPixelId);

    return (
        <noscript>
            {pixelIds.map((id) => (
                <img
                    key={id}
                    height="1"
                    width="1"
                    style={{ display: 'none' }}
                    src={`https://www.facebook.com/tr?id=${id}&ev=PageView&noscript=1`}
                />
            ))}
        </noscript>
    );
};
